import {
  EyeInvisibleOutlined,
  EyeOutlined,
  FileOutlined,
  LoadingOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Typography,
  Upload,
  message,
} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import commonApi from "utils/api";
import {
  classNames,
  getBase64,
  getPhotoThumbnailUrl,
  getFilePath,
} from "utils/common";
import { formItemLayoutHorizontal } from "utils/constants";
import shipperApi from "../../api";
const { Paragraph } = Typography;

// Helper for preview multiple images
const getPreviewList = async (files) => {
  return Promise.all(files.map((file) => getBase64(file)));
};

function ShipperDetailModal(props) {
  const [form] = Form.useForm();
  const { open, isProcessing, onSubmit, onCancel, shipper } = props;
  const [imageUrl, setImageUrl] = useState(
    getPhotoThumbnailUrl(shipper?.avatar?.path || null)
  );
  const [image, setImage] = useState(null);
  const [generatePasswordLoading, setGeneratePasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [banks, setBanks] = useState([]);

  // State for legal info images
  // CCCD
  const [identityOldPhotos, setIdentityOldPhotos] = useState([]); // [{_id, url, name}]
  const [identityNewPhotos, setIdentityNewPhotos] = useState([]); // [File]
  const [identityNewPreviews, setIdentityNewPreviews] = useState([]); // [url]
  const [deletedIdentityPhotoIds, setDeletedIdentityPhotoIds] = useState([]); // [id]
  // GPLX
  const [driverLicenseOldPhotos, setDriverLicenseOldPhotos] = useState([]);
  const [driverLicenseNewPhotos, setDriverLicenseNewPhotos] = useState([]);
  const [driverLicenseNewPreviews, setDriverLicenseNewPreviews] = useState([]);
  const [deletedDriverLicensePhotoIds, setDeletedDriverLicensePhotoIds] =
    useState([]);
  // Xe
  const [vehicleOldPhotos, setVehicleOldPhotos] = useState([]);
  const [vehicleNewPhotos, setVehicleNewPhotos] = useState([]);
  const [vehicleNewPreviews, setVehicleNewPreviews] = useState([]);
  const [deletedVehiclePhotoIds, setDeletedVehiclePhotoIds] = useState([]);
  // State for vehicle registration
  const [vehicleRegistrationOldPhotos, setVehicleRegistrationOldPhotos] =
    useState([]);
  const [vehicleRegistrationNewPhotos, setVehicleRegistrationNewPhotos] =
    useState([]);
  const [vehicleRegistrationNewPreviews, setVehicleRegistrationNewPreviews] =
    useState([]);
  const [
    deletedVehicleRegistrationPhotoIds,
    setDeletedVehicleRegistrationPhotoIds,
  ] = useState([]);

  // State for contract and rule files
  const [contractFile, setContractFile] = useState(null);
  const [contractFileList, setContractFileList] = useState([]);
  const [ruleFile, setRuleFile] = useState(null);
  const [ruleFileList, setRuleFileList] = useState([]);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [uploadingRule, setUploadingRule] = useState(false);

  // Lưu trữ URL cũ để thu hồi khi cần
  const identityPreviewUrls = useRef([]);
  const driverLicensePreviewUrls = useRef([]);
  const vehiclePreviewUrls = useRef([]);

  // Reset previews chỉ khi mở modal (open thay đổi)
  useEffect(() => {
    const getBanks = async () => {
      const data = await commonApi.getBanks();
      if (!data || data.error) {
        toast.error(data.error.msg);
      } else {
        setBanks(data);
      }
    };
    if (open) {
      getBanks();
      // Thu hồi URL cũ
      identityPreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
      driverLicensePreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
      vehiclePreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
      // CCCD
      setIdentityOldPhotos(
        props.shipper?.identityPhotos?.map((img, idx) => ({
          uid: img._id || img.id || idx,
          _id: img._id || img.id || idx,
          name: img.name || `identity_${idx}`,
          url: getPhotoThumbnailUrl(img.url || img.path || img),
        })) || []
      );
      setIdentityNewPhotos([]);
      setIdentityNewPreviews([]);
      setDeletedIdentityPhotoIds([]);
      // GPLX
      setDriverLicenseOldPhotos(
        props.shipper?.driverLicensePhotos?.map((img, idx) => ({
          uid: img._id || img.id || idx,
          _id: img._id || img.id || idx,
          name: img.name || `gplx_${idx}`,
          url: getPhotoThumbnailUrl(img.url || img.path || img),
        })) || []
      );
      setDriverLicenseNewPhotos([]);
      setDriverLicenseNewPreviews([]);
      setDeletedDriverLicensePhotoIds([]);
      // Xe
      setVehicleOldPhotos(
        props.shipper?.vehiclePhotos?.map((img, idx) => ({
          uid: img._id || img.id || idx,
          _id: img._id || img.id || idx,
          name: img.name || `vehicle_${idx}`,
          url: getPhotoThumbnailUrl(img.url || img.path || img),
        })) || []
      );
      setVehicleNewPhotos([]);
      setVehicleNewPreviews([]);
      setDeletedVehiclePhotoIds([]);
      // Đăng ký xe
      setVehicleRegistrationOldPhotos(
        props.shipper?.vehicleRegistrationPhotos?.map((img, idx) => ({
          uid: img._id || img.id || idx,
          _id: img._id || img.id || idx,
          name: img.name || `vehicle_registration_${idx}`,
          url: getPhotoThumbnailUrl(img.url || img.path || img),
        })) || []
      );
      setVehicleRegistrationNewPhotos([]);
      setVehicleRegistrationNewPreviews([]);
      setDeletedVehicleRegistrationPhotoIds([]);
      identityPreviewUrls.current = [];
      driverLicensePreviewUrls.current = [];
      vehiclePreviewUrls.current = [];
      // Load contract and rule files
      setContractFile(props.shipper?.contractFile || null);
      setRuleFile(props.shipper?.ruleFile || null);
      setContractFileList([]);
      setRuleFileList([]);
    }
  }, [open, props.shipper]);

  const initialValues = shipper
    ? {
        ...shipper,
        identityNumber: shipper.identityNumber || "",
        identityIssuedPlace: shipper.identityIssuedPlace || "",
        identityIssuedDate: shipper.identityIssuedDate
          ? dayjs(shipper.identityIssuedDate).format("YYYY-MM-DD")
          : null,
        driverLicenseNumber: shipper.driverLicenseNumber || "",
        taxCode: shipper.taxCode || "",
      }
    : {
        name: "",
        phone: "",
        email: "",
        gender: true,
        mode: "full-time",
        vehicle: "",
        numberPlate: "",
        shift: "",
        active: true,
        password: "",
        priority: 100,
        telegramId: "",
        banking: {
          bankName: "",
          accountNumber: "",
          accountName: "",
        },
        activeFood: true,
        activeBike: false,
        identityNumber: "",
        identityIssuedPlace: "",
        identityIssuedDate: null,
        driverLicenseNumber: "",
        taxCode: "",
      };

  const userInfo = useSelector((state) => state.auth.userInfo);

  // Xử lý upload mới cho từng trường
  const handleIdentityPhotoChange = ({ file, fileList }) => {
    const oldFiles = fileList.filter((f) => !f.originFileObj);
    const newFiles = fileList
      .filter((f) => f.originFileObj)
      .map((f) => f.originFileObj);
    if (oldFiles.length + newFiles.length > 2) {
      toast.error("Chỉ được chọn tối đa 2 ảnh CCCD");
      return;
    }
    // Thu hồi preview cũ
    identityNewPreviews.forEach((url) => url && URL.revokeObjectURL(url));
    setIdentityOldPhotos(oldFiles.map((f) => ({ ...f, _id: f._id || f.uid })));
    setIdentityNewPhotos(newFiles);
    setIdentityNewPreviews(
      newFiles.map((file) => (file ? URL.createObjectURL(file) : null))
    );
  };
  const handleIdentityPhotoRemove = (file) => {
    if (!file.originFileObj && file._id) {
      setDeletedIdentityPhotoIds((ids) => [...ids, file._id]);
      setIdentityOldPhotos((list) => list.filter((f) => f._id !== file._id));
    } else if (file.originFileObj) {
      setIdentityNewPhotos((list) =>
        list.filter((f) => f !== file.originFileObj)
      );
      setIdentityNewPreviews((list, idx) => list.filter((_, i) => i !== idx));
    }
    return false;
  };
  // GPLX
  const handleDriverLicensePhotoChange = ({ file, fileList }) => {
    const oldFiles = fileList.filter((f) => !f.originFileObj);
    const newFiles = fileList
      .filter((f) => f.originFileObj)
      .map((f) => f.originFileObj);
    if (oldFiles.length + newFiles.length > 2) {
      toast.error("Chỉ được chọn tối đa 2 ảnh GPLX");
      return;
    }
    driverLicenseNewPreviews.forEach((url) => url && URL.revokeObjectURL(url));
    setDriverLicenseOldPhotos(
      oldFiles.map((f) => ({ ...f, _id: f._id || f.uid }))
    );
    setDriverLicenseNewPhotos(newFiles);
    setDriverLicenseNewPreviews(
      newFiles.map((file) => (file ? URL.createObjectURL(file) : null))
    );
  };
  const handleDriverLicensePhotoRemove = (file) => {
    if (!file.originFileObj && file._id) {
      setDeletedDriverLicensePhotoIds((ids) => [...ids, file._id]);
      setDriverLicenseOldPhotos((list) =>
        list.filter((f) => f._id !== file._id)
      );
    } else if (file.originFileObj) {
      setDriverLicenseNewPhotos((list) =>
        list.filter((f) => f !== file.originFileObj)
      );
      setDriverLicenseNewPreviews((list, idx) =>
        list.filter((_, i) => i !== idx)
      );
    }
    return false;
  };
  // Xe
  const handleVehiclePhotoChange = ({ file, fileList }) => {
    const oldFiles = fileList.filter((f) => !f.originFileObj);
    const newFiles = fileList
      .filter((f) => f.originFileObj)
      .map((f) => f.originFileObj);
    if (oldFiles.length + newFiles.length > 3) {
      toast.error("Chỉ được chọn tối đa 3 ảnh xe");
      return;
    }
    vehicleNewPreviews.forEach((url) => url && URL.revokeObjectURL(url));
    setVehicleOldPhotos(oldFiles.map((f) => ({ ...f, _id: f._id || f.uid })));
    setVehicleNewPhotos(newFiles);
    setVehicleNewPreviews(
      newFiles.map((file) => (file ? URL.createObjectURL(file) : null))
    );
  };
  const handleVehiclePhotoRemove = (file) => {
    if (!file.originFileObj && file._id) {
      setDeletedVehiclePhotoIds((ids) => [...ids, file._id]);
      setVehicleOldPhotos((list) => list.filter((f) => f._id !== file._id));
    } else if (file.originFileObj) {
      setVehicleNewPhotos((list) =>
        list.filter((f) => f !== file.originFileObj)
      );
      setVehicleNewPreviews((list, idx) => list.filter((_, i) => i !== idx));
    }
    return false;
  };
  // Handlers for vehicle registration photos
  const handleVehicleRegistrationPhotoChange = ({ file, fileList }) => {
    const oldFiles = fileList.filter((f) => !f.originFileObj);
    const newFiles = fileList
      .filter((f) => f.originFileObj)
      .map((f) => f.originFileObj);
    if (oldFiles.length + newFiles.length > 2) {
      toast.error("Chỉ được chọn tối đa 2 ảnh đăng ký xe");
      return;
    }
    vehicleRegistrationNewPreviews.forEach(
      (url) => url && URL.revokeObjectURL(url)
    );
    setVehicleRegistrationOldPhotos(
      oldFiles.map((f) => ({ ...f, _id: f._id || f.uid }))
    );
    setVehicleRegistrationNewPhotos(newFiles);
    setVehicleRegistrationNewPreviews(
      newFiles.map((file) => (file ? URL.createObjectURL(file) : null))
    );
  };
  const handleVehicleRegistrationPhotoRemove = (file) => {
    if (!file.originFileObj && file._id) {
      setDeletedVehicleRegistrationPhotoIds((ids) => [...ids, file._id]);
      setVehicleRegistrationOldPhotos((list) =>
        list.filter((f) => f._id !== file._id)
      );
    } else if (file.originFileObj) {
      setVehicleRegistrationNewPhotos((list) =>
        list.filter((f) => f !== file.originFileObj)
      );
      setVehicleRegistrationNewPreviews((list, idx) =>
        list.filter((_, i) => i !== idx)
      );
    }
    return false;
  };

  // Khi submit, gửi file mới, id ảnh còn giữ, id ảnh đã xóa
  const onOK = () => {
    form
      .validateFields()
      .then(async (values) => {
        // Validate số lượng ảnh (chỉ kiểm tra tối đa)
        if (driverLicenseOldPhotos.length + driverLicenseNewPhotos.length > 2) {
          toast.error("Chỉ được chọn tối đa 2 ảnh GPLX");
          return;
        }
        const formData = new FormData();
        const shipper = { ...initialValues, ...values };
        if (shipper.identityIssuedDate && shipper.identityIssuedDate.format) {
          shipper.identityIssuedDate =
            shipper.identityIssuedDate.format("YYYY-MM-DD");
        }
        Object.entries({
          data: JSON.stringify(shipper),
          avatar: image,
        }).forEach(([key, value]) => {
          formData.append(key, value);
        });
        // Ảnh mới
        identityNewPhotos.forEach((file) => {
          formData.append("identityPhotos", file);
        });
        driverLicenseNewPhotos.forEach((file) => {
          formData.append("driverLicensePhotos", file);
        });
        vehicleNewPhotos.forEach((file) => {
          formData.append("vehiclePhotos", file);
        });
        // Ảnh cũ còn giữ lại (id)
        formData.append(
          "identityOldPhotoIds",
          JSON.stringify(identityOldPhotos.map((f) => f._id))
        );
        formData.append(
          "driverLicenseOldPhotoIds",
          JSON.stringify(driverLicenseOldPhotos.map((f) => f._id))
        );
        formData.append(
          "vehicleOldPhotoIds",
          JSON.stringify(vehicleOldPhotos.map((f) => f._id))
        );
        // Ảnh xóa
        deletedIdentityPhotoIds.forEach((id) => {
          formData.append("deletedIdentityPhotoIds", id);
        });
        deletedDriverLicensePhotoIds.forEach((id) => {
          formData.append("deletedDriverLicensePhotoIds", id);
        });
        deletedVehiclePhotoIds.forEach((id) => {
          formData.append("deletedVehiclePhotoIds", id);
        });
        // Đăng ký xe
        vehicleRegistrationNewPhotos.forEach((file) => {
          formData.append("vehicleRegistrationPhotos", file);
        });
        formData.append(
          "vehicleRegistrationOldPhotoIds",
          JSON.stringify(vehicleRegistrationOldPhotos.map((f) => f._id))
        );
        deletedVehicleRegistrationPhotoIds.forEach((id) => {
          formData.append("deletedVehicleRegistrationPhotoIds", id);
        });
        onSubmit(shipper, formData);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  // Handlers for contract and rule files
  const handleContractUpload = async (file) => {
    const isPDF = file.type === "application/pdf";
    if (!isPDF) {
      message.error("Chỉ chấp nhận file PDF!");
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("File không được vượt quá 10MB!");
      return Upload.LIST_IGNORE;
    }
    setContractFileList([file]);
    return false;
  };

  const handleContractRemove = () => {
    setContractFileList([]);
  };

  const handleRuleUpload = async (file) => {
    const isPDF = file.type === "application/pdf";
    if (!isPDF) {
      message.error("Chỉ chấp nhận file PDF!");
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("File không được vượt quá 10MB!");
      return Upload.LIST_IGNORE;
    }
    setRuleFileList([file]);
    return false;
  };

  const handleRuleRemove = () => {
    setRuleFileList([]);
  };

  const handleUploadContract = async () => {
    if (contractFileList.length === 0) {
      message.error("Vui lòng chọn file hợp đồng!");
      return;
    }
    if (!shipper?._id) {
      message.error("Không tìm thấy thông tin tài xế!");
      return;
    }
    try {
      setUploadingContract(true);
      const formData = new FormData();
      formData.append("file", contractFileList[0]);
      const data = await shipperApi.uploadContract(shipper._id, formData);
      if (data.error) {
        toast.error(data.error.msg || "Lỗi khi upload hợp đồng!");
      } else {
        toast.success("Upload hợp đồng thành công!");
        setContractFile(data.data?.contractFile || null);
        setContractFileList([]);
        // Refresh shipper data
        if (onSubmit) {
          // Trigger refresh
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Lỗi khi upload hợp đồng!");
    } finally {
      setUploadingContract(false);
    }
  };

  const handleUploadRule = async () => {
    if (ruleFileList.length === 0) {
      message.error("Vui lòng chọn file quy tắc ứng xử!");
      return;
    }
    if (!shipper?._id) {
      message.error("Không tìm thấy thông tin tài xế!");
      return;
    }
    try {
      setUploadingRule(true);
      const formData = new FormData();
      formData.append("file", ruleFileList[0]);
      const data = await shipperApi.uploadRule(shipper._id, formData);
      if (data.error) {
        toast.error(data.error.msg || "Lỗi khi upload quy tắc ứng xử!");
      } else {
        toast.success("Upload quy tắc ứng xử thành công!");
        setRuleFile(data.data?.ruleFile || null);
        setRuleFileList([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Lỗi khi upload quy tắc ứng xử!");
    } finally {
      setUploadingRule(false);
    }
  };

  const onGeneratePassword = async () => {
    try {
      setGeneratePasswordLoading(true);
      const data = await commonApi.generatePassword();
      if (data.error) {
        toast.error(data.error.msg);
      } else {
        toast.success("Tạo mật khẩu thành công");
        form.setFieldValue("password", data.password);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setGeneratePasswordLoading(false);
    }
  };

  const beforeUpload = async (file) => {
    const validType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    if (!validType) {
      toast.error("Chỉ chấp nhận file ảnh!");
    } else {
      const preview = await getBase64(file);
      setImageUrl(preview);
      setImage(file);
    }
    return false;
  };

  const onValueChange = (changedValues) => {
    if (changedValues["banking"]?.["accountName"]) {
      form.setFieldValue(
        ["banking", "accountName"],
        changedValues["banking"]["accountName"].toUpperCase()
      );
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // Upload handlers cho từng trường, chỉ set state của trường đó
  // handleIdentityPhotoChange, handleDriverLicensePhotoChange, handleVehiclePhotoChange đã được tách ra
  // handleIdentityPhotoRemove, handleDriverLicensePhotoRemove, handleVehiclePhotoRemove đã được tách ra

  // Cleanup toàn bộ URL khi component unmount
  useEffect(() => {
    return () => {
      identityPreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
      driverLicensePreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
      vehiclePreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
    };
  }, []);

  // Render fileList cho Upload
  const identityFileList = [
    ...identityOldPhotos.map((f) => ({ ...f, status: "done" })),
    ...identityNewPhotos.map((file, idx) => ({
      uid: `new_${idx}`,
      name: file.name,
      status: "done",
      url: identityNewPreviews[idx],
      originFileObj: file,
    })),
  ];
  const driverLicenseFileList = [
    ...driverLicenseOldPhotos.map((f) => ({ ...f, status: "done" })),
    ...driverLicenseNewPhotos.map((file, idx) => ({
      uid: `new_${idx}`,
      name: file.name,
      status: "done",
      url: driverLicenseNewPreviews[idx],
      originFileObj: file,
    })),
  ];
  const vehicleFileList = [
    ...vehicleOldPhotos.map((f) => ({ ...f, status: "done" })),
    ...vehicleNewPhotos.map((file, idx) => ({
      uid: `new_${idx}`,
      name: file.name,
      status: "done",
      url: vehicleNewPreviews[idx],
      originFileObj: file,
    })),
  ];

  return (
    <Modal
      title="Tài xế"
      okText="Lưu"
      cancelText="Huỷ"
      centered
      open={open}
      onOk={onOK}
      onCancel={onCancel}
      confirmLoading={isProcessing}
      width="40%"
    >
      <Form
        {...formItemLayoutHorizontal}
        form={form}
        initialValues={initialValues}
        onValuesChange={onValueChange}
      >
        <Tabs defaultActiveKey="info">
          <Tabs.TabPane tab="Thông tin" key="info">
            <Form.Item
              name="name"
              label="Tên"
              rules={[
                {
                  required: true,
                  message: "Trường này là bắt buộc",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="phone"
              label="SĐT"
              rules={[
                {
                  required: true,
                  message: "Trường này là bắt buộc",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input type="email" />
            </Form.Item>
            <Form.Item
              name="gender"
              label="Giới tính"
              rules={[{ required: true }]}
            >
              <Select placeholder="--Giới tính--">
                <Select.Option value={true}>Nam</Select.Option>
                <Select.Option value={false}>Nữ</Select.Option>
              </Select>
            </Form.Item>
            {/* <Form.Item name="mode" label="Chế độ">
          <Radio.Group>
            <Radio value="full-time">Full-time</Radio>
            <Radio value="part-time">Part-time</Radio>
          </Radio.Group>
        </Form.Item> */}
            <Form.Item
              name="vehicle"
              label="Loại xe"
              rules={[
                {
                  required: true,
                  message: "Trường này là bắt buộc",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="numberPlate"
              label="Biển số xe"
              rules={[
                {
                  required: true,
                  message: "Trường này là bắt buộc",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="shift" label="Ca làm việc">
              <Input />
            </Form.Item>
            <Form.Item label="Ảnh đại diện">
              <Upload
                accept={"image/*"}
                name="avatar"
                listType="picture-card"
                className="avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeUpload}
              >
                {imageUrl ? (
                  <img src={imageUrl} alt="image" style={{ width: "100%" }} />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Tài khoản" key="account">
            <Form.Item label="Đơn được nhận" style={{ marginBottom: 0 }}>
              <Form.Item
                name="activeFood"
                valuePropName="checked"
                style={{
                  display: "inline-block",
                  marginRight: "20px",
                }}
              >
                <Switch
                  checkedChildren="GOKA Food"
                  unCheckedChildren="GOKA Food"
                />
              </Form.Item>
              <Form.Item
                name="activeBike"
                valuePropName="checked"
                style={{
                  display: "inline-block",
                  marginRight: "20px",
                }}
              >
                <Switch
                  checkedChildren="GOKA Bike"
                  unCheckedChildren="GOKA Bike"
                />
              </Form.Item>
              <Form.Item
                name="activeExpress"
                valuePropName="checked"
                style={{
                  display: "inline-block",
                  marginRight: "20px",
                }}
              >
                <Switch
                  checkedChildren="GOKA Giao hàng"
                  unCheckedChildren="GOKA Giao hàng"
                />
              </Form.Item>
            </Form.Item>
            <Form.Item name="active" label="Trạng thái">
              <Select>
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>In-active</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="password" label="Mật khẩu">
              <Input
                // disabled={Boolean(shipper)}
                type={showPassword ? "text" : "password"}
                addonBefore={
                  <div
                    className={
                      "select-none cursor-pointer flex items-center gap-x-2"
                    }
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </div>
                }
                addonAfter={
                  generatePasswordLoading ? (
                    <LoadingOutlined spin />
                  ) : (
                    <div
                      className={classNames(
                        "select-none",
                        shipper ? "cursor-pointer" : "cursor-pointer"
                      )}
                      onClick={onGeneratePassword}
                    >
                      Tạo mật khẩu
                    </div>
                  )
                }
              />
            </Form.Item>
            <Form.Item
              name="priority"
              label="Độ ưu tiên"
              rules={[
                {
                  required: true,
                  message: "Trường này là bắt buộc",
                },
              ]}
            >
              <Input type="number" min={1} max={100} />
            </Form.Item>
            <Form.Item name="telegramId" label="Telegram ID">
              <Input />
            </Form.Item>
            {userInfo?.role !== "manager" && (
              <Form.Item name="balance" label="Số dư ví">
                <Input readOnly />
              </Form.Item>
            )}
            {userInfo?.role !== "manager" && (
              <Form.Item name="withdrawCode" label="Mã nạp tiền">
                <Input readOnly />
              </Form.Item>
            )}
            {shipper ? (
              <Form.Item name="appToken" label="FCM Token">
                {shipper.appTokens?.map((token) => (
                  <Paragraph copyable key={token}>
                    {token}
                  </Paragraph>
                )) || null}
              </Form.Item>
            ) : null}
          </Tabs.TabPane>
          <Tabs.TabPane tab="Tài khoản ngân hàng" key="bank">
            <Form.Item name={["banking", "bankId"]} label="Ngân hàng">
              <Select
                showSearch
                optionFilterProp="label"
                options={banks.map((bank) => ({
                  value: bank.id,
                  label: bank.name,
                }))}
              ></Select>
            </Form.Item>
            <Form.Item name={["banking", "accountNumber"]} label="Số tài khoản">
              <Input />
            </Form.Item>
            <Form.Item name={["banking", "accountName"]} label="Chủ tài khoản">
              <Input />
            </Form.Item>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Thông tin pháp lý" key="legal">
            <Form.Item name="identityNumber" label="Số CCCD">
              <Input />
            </Form.Item>
            <Form.Item name="identityIssuedDate" label="Ngày cấp CCCD">
              <Input type="date" />
            </Form.Item>
            <Form.Item name="identityIssuedPlace" label="Nơi cấp CCCD">
              <Input />
            </Form.Item>
            <Form.Item label="Ảnh CCCD">
              <Upload
                accept="image/*"
                listType="picture-card"
                fileList={identityFileList}
                onChange={handleIdentityPhotoChange}
                onRemove={handleIdentityPhotoRemove}
                multiple
              >
                {identityOldPhotos.length < 2 && uploadButton}
              </Upload>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                Có thể chọn tối đa 2 ảnh: mặt trước và mặt sau CCCD.
              </div>
            </Form.Item>
            <Form.Item name="driverLicenseNumber" label="Số GPLX">
              <Input />
            </Form.Item>
            <Form.Item label="Ảnh GPLX">
              <Upload
                accept="image/*"
                listType="picture-card"
                fileList={driverLicenseFileList}
                onChange={handleDriverLicensePhotoChange}
                onRemove={handleDriverLicensePhotoRemove}
                multiple
              >
                {driverLicenseOldPhotos.length < 2 && uploadButton}
              </Upload>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                Tối đa 2 ảnh: mặt trước và mặt sau GPLX.
              </div>
            </Form.Item>
            <Form.Item name="vehicleRegistrationNumber" label="Số đăng ký xe">
              <Input />
            </Form.Item>
            <Form.Item label="Ảnh đăng ký xe">
              <Upload
                accept="image/*"
                listType="picture-card"
                fileList={[
                  ...vehicleRegistrationOldPhotos.map((f) => ({
                    ...f,
                    status: "done",
                  })),
                  ...vehicleRegistrationNewPhotos.map((file, idx) => ({
                    uid: `new_${idx}`,
                    name: file.name,
                    status: "done",
                    url: vehicleRegistrationNewPreviews[idx],
                    originFileObj: file,
                  })),
                ]}
                onChange={handleVehicleRegistrationPhotoChange}
                onRemove={handleVehicleRegistrationPhotoRemove}
                multiple
              >
                {vehicleRegistrationOldPhotos.length +
                  vehicleRegistrationNewPhotos.length <
                  2 && uploadButton}
              </Upload>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                Tối đa 2 ảnh đăng ký xe.
              </div>
            </Form.Item>
            <Form.Item name="taxCode" label="Mã số thuế">
              <Input />
            </Form.Item>
            <Form.Item label="Ảnh xe">
              <Upload
                accept="image/*"
                listType="picture-card"
                fileList={vehicleFileList}
                onChange={handleVehiclePhotoChange}
                onRemove={handleVehiclePhotoRemove}
                multiple
              >
                {vehicleOldPhotos.length < 3 && uploadButton}
              </Upload>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                Tối đa 3 ảnh xe.
              </div>
            </Form.Item>
          </Tabs.TabPane>
          {shipper?._id && (
            <Tabs.TabPane tab="Hợp đồng" key="contract">
              <div>
                {contractFile && (
                  <div style={{ marginBottom: 16 }}>
                    <Space size="large" style={{ marginBottom: 8 }}>
                      <a
                        href={getFilePath(contractFile)}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        <FileOutlined /> Tải xuống file hợp đồng
                      </a>
                      <a
                        href={getFilePath(
                          contractFile.replace("uploads", "uploads/preview")
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <EyeOutlined /> Xem trong tab mới
                      </a>
                    </Space>
                    <object
                      data={`${getFilePath(
                        contractFile.replace("uploads", "uploads/preview")
                      )}#toolbar=0`}
                      type="application/pdf"
                      style={{
                        width: "100%",
                        height: "500px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                      }}
                    >
                      <div style={{ padding: 20, textAlign: "center" }}>
                        <p>Không thể hiển thị PDF. Vui lòng</p>
                        <a
                          href={getFilePath(
                            contractFile.replace("uploads", "uploads/preview")
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          mở trong tab mới
                        </a>
                        {" hoặc "}
                        <a href={getFilePath(contractFile)} download>
                          tải xuống
                        </a>
                      </div>
                    </object>
                  </div>
                )}
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Upload file hợp đồng mới (PDF, tối đa 10MB)</strong>
                  </div>
                  <Upload
                    beforeUpload={handleContractUpload}
                    onRemove={handleContractRemove}
                    fileList={contractFileList}
                    accept=".pdf,application/pdf"
                    maxCount={1}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      disabled={uploadingContract}
                    >
                      Chọn file
                    </Button>
                  </Upload>
                  {contractFileList.length > 0 && (
                    <Button
                      type="primary"
                      onClick={handleUploadContract}
                      loading={uploadingContract}
                      style={{ marginTop: 8 }}
                    >
                      Upload hợp đồng
                    </Button>
                  )}
                </div>
              </div>
            </Tabs.TabPane>
          )}
          {shipper?._id && (
            <Tabs.TabPane tab="Quy tắc ứng xử" key="rule">
              <div>
                {ruleFile && (
                  <div style={{ marginBottom: 16 }}>
                    <Space size="large" style={{ marginBottom: 8 }}>
                      <a
                        href={getFilePath(ruleFile)}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        <FileOutlined /> Tải xuống file quy tắc ứng xử
                      </a>
                      <a
                        href={getFilePath(
                          ruleFile.replace("uploads", "uploads/preview")
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <EyeOutlined /> Xem trong tab mới
                      </a>
                    </Space>
                    <object
                      data={`${getFilePath(
                        ruleFile.replace("uploads", "uploads/preview")
                      )}#toolbar=0`}
                      type="application/pdf"
                      style={{
                        width: "100%",
                        height: "500px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                      }}
                    >
                      <div style={{ padding: 20, textAlign: "center" }}>
                        <p>Không thể hiển thị PDF. Vui lòng</p>
                        <a
                          href={getFilePath(
                            ruleFile.replace("uploads", "uploads/preview")
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          mở trong tab mới
                        </a>
                        {" hoặc "}
                        <a href={getFilePath(ruleFile)} download>
                          tải xuống
                        </a>
                      </div>
                    </object>
                  </div>
                )}
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>
                      Upload file quy tắc ứng xử mới (PDF, tối đa 10MB)
                    </strong>
                  </div>
                  <Upload
                    beforeUpload={handleRuleUpload}
                    onRemove={handleRuleRemove}
                    fileList={ruleFileList}
                    accept=".pdf,application/pdf"
                    maxCount={1}
                  >
                    <Button icon={<UploadOutlined />} disabled={uploadingRule}>
                      Chọn file
                    </Button>
                  </Upload>
                  {ruleFileList.length > 0 && (
                    <Button
                      type="primary"
                      onClick={handleUploadRule}
                      loading={uploadingRule}
                      style={{ marginTop: 8 }}
                    >
                      Upload quy tắc ứng xử
                    </Button>
                  )}
                </div>
              </div>
            </Tabs.TabPane>
          )}
        </Tabs>
      </Form>
    </Modal>
  );
}

ShipperDetailModal.propTypes = {
  open: PropTypes.bool,
  isProcessing: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  shipper: PropTypes.object,
};

ShipperDetailModal.defaultProps = {
  isProcessing: false,
  open: false,
  shipper: null,
};

export default ShipperDetailModal;
